// ETAPA 2 - Criar hook de dados

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/infra/supabase/client";
import {
  Temple,
  TempleProgress,
  TempleWithProgress,
  TempleJourneyStats,
  TempleColor,
  TempleStatus,
} from "@/types/temple.types";
import {
  getUserTempleGate,
  type UserTempleGate,
} from "@/data/admin/turmas.service";

/**
 * Aplica o gating da turma sobre o status individual de progresso.
 * - Sem turma (applies=false): retorna o status original (comportamento legado).
 * - Modo entry: antes do start_date, tudo trava; depois, progresso individual governa.
 * - Modo batch: templo fechado pela turma trava; templo aberto promove 'locked' -> 'unlocked'.
 */
function applyTurmaGate(
  status: TempleStatus,
  templeId: number,
  gate: UserTempleGate,
): TempleStatus {
  if (!gate.applies) return status;
  if (gate.mode === "entry") {
    return gate.entryOpen ? status : "locked";
  }
  // batch
  const open = gate.openByTemple.get(templeId) ?? false;
  if (!open) return "locked";
  return status === "locked" ? "unlocked" : status;
}

// Função para determinar cor do templo baseado no status e order_number
function getTempleColor(
  status: TempleStatus,
  orderNumber: number,
): TempleColor {
  if (status === "completed") return "green";
  if (status === "locked") return "gray";

  // Ativo: azul para 1-3, laranja para 4, azul para resto
  if (orderNumber === 4) return "orange";
  return "blue";
}

// Função para determinar texto do botão
function getButtonText(status: TempleStatus, completedTasks: number): string {
  switch (status) {
    case "completed":
      return "Revisar";
    case "in_progress":
      return completedTasks > 0 ? "Continuar" : "Iniciar Templo";
    case "unlocked":
      return "Iniciar Templo";
    case "locked":
      return "Bloqueado";
    default:
      return "Bloqueado";
  }
}

// Função para determinar variante do botão
function getButtonVariant(
  status: TempleStatus,
): "default" | "secondary" | "outline" | "ghost" {
  switch (status) {
    case "completed":
      return "outline"; // Verde
    case "in_progress":
    case "unlocked":
      return "default"; // Primário (dourado)
    case "locked":
      return "ghost"; // Desabilitado
    default:
      return "ghost";
  }
}

// Fetch de templos com progresso do usuário
async function fetchTempleJourney(
  userId: string,
): Promise<TempleWithProgress[]> {
  // 1. Buscar todos os templos (ordenados)
  const { data: temples, error: templesError } = await supabase
    .from("temples")
    .select("*")
    .eq("active", true)
    .order("order_number", { ascending: true });

  if (templesError) throw templesError;
  if (!temples) throw new Error("Nenhum templo encontrado");

  // 2. Buscar progresso do usuário em todos os templos
  const { data: progressList, error: progressError } = await supabase
    .from("temple_progress")
    .select("*")
    .eq("user_id", userId);

  if (progressError) throw progressError;

  // 3. Se o usuário não tem nenhum progresso, inicializar o primeiro templo
  if (!progressList || progressList.length === 0) {
    const firstTemple = temples[0];
    if (firstTemple) {
      const { data: newProgress, error: createError } = await supabase
        .from("temple_progress")
        .insert({
          user_id: userId,
          temple_id: firstTemple.id,
          status: "unlocked",
          progress_percentage: 0,
          completed_tasks: 0,
          total_temple_tasks: firstTemple.total_tasks || 0,
          earned_xp: 0,
          time_invested_minutes: 0,
        })
        .select()
        .single();

      if (createError) {
        console.error("Erro ao criar progresso inicial:", createError);
      } else {
        // Atualizar a lista de progresso com o novo registro
        progressList?.push(newProgress);
      }
    }
  }

  // 3.5 Gate da turma (se o aluno pertence a uma turma com liberacao controlada)
  let gate: UserTempleGate;
  try {
    gate = await getUserTempleGate(userId);
  } catch (err) {
    // Falha no gate nao deve quebrar a jornada: cai no comportamento legado.
    console.error("Erro ao calcular gate da turma:", err);
    gate = {
      applies: false,
      mode: null,
      entryOpen: true,
      openByTemple: new Map(),
    };
  }

  // 4. Mapear templos com progresso
  const templesWithProgress: TempleWithProgress[] = temples.map((temple) => {
    const progress =
      progressList?.find((p) => p.temple_id === temple.id) || null;

    const rawStatus = progress?.status || "locked";
    const status = applyTurmaGate(rawStatus, temple.id, gate);
    const completedTasks = progress?.completed_tasks || 0;

    return {
      ...temple,
      progress,
      isLocked: status === "locked",
      isActive: status === "in_progress" || status === "unlocked",
      isCompleted: status === "completed",
      color: getTempleColor(status, temple.order_number),
      buttonText: getButtonText(status, completedTasks),
      buttonVariant: getButtonVariant(status),
    };
  });

  return templesWithProgress;
}

// Calcular estatísticas gerais
function calculateStats(temples: TempleWithProgress[]): TempleJourneyStats {
  const completed = temples.filter((t) => t.isCompleted).length;
  const active = temples.filter((t) => t.isActive).length;
  const blocked = temples.filter((t) => t.isLocked).length;

  const totalXp = temples.reduce(
    (sum, t) => sum + (t.progress?.earned_xp || 0),
    0,
  );
  const totalTime = temples.reduce(
    (sum, t) => sum + (t.progress?.time_invested_minutes || 0),
    0,
  );

  const overallProgress = Math.round((completed / temples.length) * 100);

  return {
    totalTemples: temples.length,
    completedTemples: completed,
    activeTemples: active,
    blockedTemples: blocked,
    overallProgress,
    totalXpEarned: totalXp,
    totalTimeInvested: totalTime,
  };
}

// Hook principal
export function useTempleJourney(userId: string) {
  const query = useQuery({
    queryKey: ["temple-journey", userId],
    queryFn: () => fetchTempleJourney(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  });

  const stats = query.data ? calculateStats(query.data) : null;
  const currentTemple = query.data?.find((t) => t.isActive);

  return {
    temples: query.data || [],
    stats,
    currentTemple,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
